import {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  BrowserProvider,
  Contract,
  JsonRpcProvider,
  formatUnits,
  parseUnits,
} from "ethers";

import {
  useAppKitAccount,
  useAppKitProvider,
  useAppKitNetwork,
} from "@reown/appkit/react";

import {
  KTL_ABI,
  TOKEN_ABI,
} from "../contracts";

/* =========================================================
   CONFIGURATION
========================================================= */

const BSC_CHAIN_ID = 56;

const BSC_RPC =
  process.env.REACT_APP_BSC_RPC_URL ||
  "https://bsc-dataseed.bnbchain.org";

const KTL_CONTRACT_ADDRESS =
  process.env.REACT_APP_KTL_CONTRACT_ADDRESS ||
  "0x1aE576e6DA93b7B2727B108b7CbF964e9180b235";

const KIND_TOKEN_ADDRESS =
  process.env.REACT_APP_KIND_TOKEN_ADDRESS ||
  "0x41f52A42091A6B2146561bF05b722Ad1d0e46f8b";

const USD_DECIMALS = 18;

/* =========================================================
   HELPERS
========================================================= */

const toNumberSafe = (
  value,
  decimals = 18
) => {
  try {
    return Number(
      formatUnits(
        value || 0n,
        decimals
      )
    );
  } catch {
    return 0;
  }
};

const toStringSafe = (
  value,
  decimals = 18
) => {
  try {
    return formatUnits(
      value || 0n,
      decimals
    );
  } catch {
    return "0";
  }
};

const normaliseError = (error) => {
  return (
    error?.shortMessage ||
    error?.reason ||
    error?.info?.error?.message ||
    error?.data?.message ||
    error?.message ||
    "Unknown contract error"
  );
};

/* =========================================================
   HOOK
========================================================= */

function useContract() {
  /* =======================================================
     REOWN APPKIT ACCOUNT
  ======================================================= */

  const {
    address,
    isConnected,
  } = useAppKitAccount();

  /* =======================================================
     REOWN APPKIT EVM PROVIDER

     This is the important part.

     Bitget Wallet
     MetaMask
     WalletConnect wallets
     Other EVM wallets

     are handled by AppKit.
  ======================================================= */

  const {
    walletProvider,
  } = useAppKitProvider("eip155");

  /* =======================================================
     REOWN NETWORK
  ======================================================= */

  const {
    chainId: appKitChainId,
  } = useAppKitNetwork();

  /* =======================================================
     LOCAL STATE
  ======================================================= */

  const [chainId, setChainId] =
    useState(null);

  /* =======================================================
     KEEP CHAIN ID IN SYNC
  ======================================================= */

  useEffect(() => {
    if (appKitChainId) {
      setChainId(
        Number(appKitChainId)
      );
    } else {
      setChainId(null);
    }
  }, [appKitChainId]);

  /* =======================================================
     CHECK WALLET

     AppKit owns wallet state.
  ======================================================= */

  const checkWallet =
    useCallback(async () => {
      try {
        if (
          appKitChainId !==
          undefined &&
          appKitChainId !== null
        ) {
          setChainId(
            Number(appKitChainId)
          );
        }
      } catch (error) {
        console.error(
          "Wallet check error:",
          normaliseError(error)
        );
      }
    }, [appKitChainId]);

  useEffect(() => {
    checkWallet();
  }, [checkWallet]);

  /* =======================================================
     CONNECT WALLET

     Navbar/AppKit handles the actual modal.

     This function is kept for compatibility with
     existing components.
  ======================================================= */

  const connectWallet =
    useCallback(async () => {
      if (isConnected && address) {
        return address;
      }

      throw new Error(
        "Please use the Connect Wallet button to open AppKit."
      );
    }, [
      address,
      isConnected,
    ]);

  /* =======================================================
     DISCONNECT WALLET

     AppKit controls the actual connection.

     Keep this local compatibility function because
     existing components may call disconnectWallet().
  ======================================================= */

  const disconnectWallet =
    useCallback(() => {
    }, []);

  /* =======================================================
     READ PROVIDER

     IMPORTANT:

     Read operations use your BSC RPC.

     This does NOT depend on Bitget/MetaMask.
  ======================================================= */

  const getReadProvider =
    useCallback(() => {
      return new JsonRpcProvider(
        BSC_RPC,
        BSC_CHAIN_ID
      );
    }, []);

  /* =======================================================
     APPKIT BROWSER PROVIDER

     Connected wallet provider.

     Works with:
     - Bitget Wallet
     - MetaMask
     - WalletConnect
     - other AppKit EVM wallets
  ======================================================= */

  const getBrowserProvider =
    useCallback(async () => {
      if (!walletProvider) {
        throw new Error(
          "Wallet provider not available. Please connect your wallet."
        );
      }

      return new BrowserProvider(
        walletProvider
      );
    }, [walletProvider]);

  /* =======================================================
     SIGNER
  ======================================================= */

  const getSigner =
    useCallback(async () => {
      if (!isConnected || !address) {
        throw new Error(
          "Wallet is not connected"
        );
      }

      if (!walletProvider) {
        throw new Error(
          "Wallet provider not available"
        );
      }

      const currentChain =
        Number(
          appKitChainId || chainId || 0
        );

      if (
        currentChain !==
        BSC_CHAIN_ID
      ) {
        throw new Error(
          "Please connect to BNB Smart Chain"
        );
      }

      const browserProvider =
        await getBrowserProvider();

      return browserProvider.getSigner(
        address
      );
    }, [
      address,
      appKitChainId,
      chainId,
      getBrowserProvider,
      isConnected,
      walletProvider,
    ]);

  /* =======================================================
     CONTRACT INSTANCES
  ======================================================= */

  const getReadKtlContract =
    useCallback(() => {
      const provider =
        getReadProvider();

      return new Contract(
        KTL_CONTRACT_ADDRESS,
        KTL_ABI,
        provider
      );
    }, [
      getReadProvider,
    ]);

  const getWriteKtlContract =
    useCallback(async () => {
      const signer =
        await getSigner();

      return new Contract(
        KTL_CONTRACT_ADDRESS,
        KTL_ABI,
        signer
      );
    }, [
      getSigner,
    ]);

  const getTokenContract =
    useCallback(
      async (
        tokenAddress =
          KIND_TOKEN_ADDRESS,
        write = false
      ) => {
        const runner = write
          ? await getSigner()
          : getReadProvider();

        return new Contract(
          tokenAddress,
          TOKEN_ABI,
          runner
        );
      },
      [
        getReadProvider,
        getSigner,
      ]
    );

  /* =======================================================
     GENERAL WALLET DATA
  ======================================================= */

  const getConnectedAddress =
    useCallback(() => {
      return address || null;
    }, [address]);

  const getNativeBalance =
    useCallback(
      async (
        walletAddress = address
      ) => {
        if (!walletAddress) {
          return "0";
        }

        try {
          /*
           * Use BSC RPC for balance reads.
           * This avoids depending on Bitget RPC.
           */
          const provider =
            getReadProvider();

          const balance =
            await provider.getBalance(
              walletAddress
            );

          return formatUnits(
            balance,
            18
          );
        } catch (error) {
          console.error(
            "getNativeBalance:",
            normaliseError(error)
          );

          return "0";
        }
      },
      [
        address,
        getReadProvider,
      ]
    );

  const getTokenMetadata =
    useCallback(
      async (
        tokenAddress =
          KIND_TOKEN_ADDRESS
      ) => {
        try {
          const token =
            await getTokenContract(
              tokenAddress
            );

          const [
            symbol,
            decimals,
          ] = await Promise.all([
            token.symbol(),
            token.decimals(),
          ]);

          return {
            address:
              tokenAddress,
            symbol,
            decimals:
              Number(decimals),
          };
        } catch (error) {
          console.error(
            "getTokenMetadata:",
            normaliseError(error)
          );

          return {
            address:
              tokenAddress,
            symbol: "KIND",
            decimals: 18,
          };
        }
      },
      [
        getTokenContract,
      ]
    );

  const getTokenBalance =
    useCallback(
      async (
        walletAddress = address,
        tokenAddress =
          KIND_TOKEN_ADDRESS
      ) => {
        if (!walletAddress) {
          return {
            raw: 0n,
            formatted: "0",
          };
        }

        try {
          const token =
            await getTokenContract(
              tokenAddress
            );

          const [
            balance,
            decimals,
          ] = await Promise.all([
            token.balanceOf(
              walletAddress
            ),
            token.decimals(),
          ]);

          return {
            raw: balance,
            formatted:
              formatUnits(
                balance,
                Number(decimals)
              ),
          };
        } catch (error) {
          console.error(
            "getTokenBalance:",
            normaliseError(error)
          );

          return {
            raw: 0n,
            formatted: "0",
          };
        }
      },
      [
        address,
        getTokenContract,
      ]
    );

  /* =========================================================
     LEAGUE
  ========================================================= */

  const getCurrentLeagueId =
    useCallback(async () => {
      try {
        const contract =
          getReadKtlContract();

        return await contract.currentLeagueId();
      } catch (error) {
        console.error(
          "getCurrentLeagueId:",
          normaliseError(error)
        );

        return 0n;
      }
    }, [
      getReadKtlContract,
    ]);

  const getLeague =
    useCallback(
      async (leagueId) => {
        try {
          const contract =
            getReadKtlContract();

          return await contract.getLeague(
            leagueId
          );
        } catch (error) {
          console.error(
            "getLeague:",
            normaliseError(error)
          );

          return null;
        }
      },
      [
        getReadKtlContract,
      ]
    );

  const getCurrentLeague =
    useCallback(async () => {
      const currentLeagueId =
        await getCurrentLeagueId();

      if (
        currentLeagueId === 0n
      ) {
        return {
          leagueId:
            currentLeagueId,
          league: null,
        };
      }

      const league =
        await getLeague(
          currentLeagueId
        );

      return {
        leagueId:
          currentLeagueId,
        league,
      };
    }, [
      getCurrentLeagueId,
      getLeague,
    ]);

  const getGlobalStats =
    useCallback(async () => {
      try {
        const contract =
          getReadKtlContract();

        return await contract.getGlobalStats();
      } catch (error) {
        console.error(
          "getGlobalStats:",
          normaliseError(error)
        );

        return null;
      }
    }, [
      getReadKtlContract,
    ]);

  const getExtendedGlobalStats =
    useCallback(async () => {
      try {
        const contract =
          getReadKtlContract();

        return await contract.getExtendedGlobalStats();
      } catch (error) {
        console.error(
          "getExtendedGlobalStats:",
          normaliseError(error)
        );

        return null;
      }
    }, [
      getReadKtlContract,
    ]);

  const getGlobalTradingGoal =
    useCallback(async () => {
      try {
        const contract =
          getReadKtlContract();

        return await contract.globalTradingGoalUsd();
      } catch (error) {
        console.error(
          "getGlobalTradingGoal:",
          normaliseError(error)
        );

        return 0n;
      }
    }, [
      getReadKtlContract,
    ]);

  const getRewardTokenAddress =
    useCallback(async () => {
      try {
        const contract =
          getReadKtlContract();

        return await contract.rewardToken();
      } catch (error) {
        console.error(
          "getRewardTokenAddress:",
          normaliseError(error)
        );

        return KIND_TOKEN_ADDRESS;
      }
    }, [
      getReadKtlContract,
    ]);

  const getRewardTokenData =
    useCallback(async () => {
      const tokenAddress =
        await getRewardTokenAddress();

      return getTokenMetadata(
        tokenAddress
      );
    }, [
      getRewardTokenAddress,
      getTokenMetadata,
    ]);

  /* =========================================================
     USER
  ========================================================= */

  const getWalletData =
    useCallback(
      async (
        walletAddress = address
      ) => {
        if (!walletAddress) {
          return null;
        }

        try {
          const contract =
            getReadKtlContract();

          return await contract.getWallet(
            walletAddress
          );
        } catch (error) {
          console.error(
            "getWalletData:",
            normaliseError(error)
          );

          return null;
        }
      },
      [
        address,
        getReadKtlContract,
      ]
    );

  const getWalletKpBreakdown =
    useCallback(
      async (
        walletAddress = address
      ) => {
        if (!walletAddress) {
          return null;
        }

        try {
          const contract =
            getReadKtlContract();

          return await contract.getWalletKpBreakdown(
            walletAddress
          );
        } catch (error) {
          console.error(
            "getWalletKpBreakdown:",
            normaliseError(error)
          );

          return null;
        }
      },
      [
        address,
        getReadKtlContract,
      ]
    );

  const getLeagueUserStats =
    useCallback(
      async (
        leagueId,
        walletAddress = address
      ) => {
        if (
          !walletAddress ||
          !leagueId
        ) {
          return null;
        }

        try {
          const contract =
            getReadKtlContract();

          return await contract.getLeagueUserStats(
            leagueId,
            walletAddress
          );
        } catch (error) {
          console.error(
            "getLeagueUserStats:",
            normaliseError(error)
          );

          return null;
        }
      },
      [
        address,
        getReadKtlContract,
      ]
    );

  const getUserDashboardData =
    useCallback(
      async (
        walletAddress = address
      ) => {
        if (!walletAddress) {
          return null;
        }

        try {
          const contract =
            getReadKtlContract();

          const currentLeagueId =
            await contract.currentLeagueId();

          const [
            league,
            wallet,
            kpBreakdown,
            estimatedReward,
            claimStatus,
            globalStats,
            extendedStats,
            globalGoal,
            rewardTokenAddress,
          ] = await Promise.all([
            contract.getLeague(
              currentLeagueId
            ),

            contract.getWallet(
              walletAddress
            ),

            contract.getWalletKpBreakdown(
              walletAddress
            ),

            contract.getEstimatedReward(
              currentLeagueId,
              walletAddress
            ),

            contract.getClaimStatus(
              currentLeagueId,
              walletAddress
            ),

            contract.getGlobalStats(),

            contract.getExtendedGlobalStats(),

            contract.globalTradingGoalUsd(),

            contract.rewardToken(),
          ]);

          const rewardToken =
            await getTokenMetadata(
              rewardTokenAddress
            );

          const tierId =
            Number(
              estimatedReward?.tierId ||
              wallet?.tier ||
              0
            );

          let tier = null;

          if (tierId > 0) {
            tier =
              await contract.getTier(
                tierId
              );
          }

          return {
            currentLeagueId,
            league,
            wallet,
            kpBreakdown,
            estimatedReward,
            globalTradingGoalUsd:
              globalGoal,
            claimStatus,
            globalStats,
            extendedStats,
            globalGoal,
            rewardToken,
            tierId,
            tier,
          };
        } catch (error) {
          console.error(
            "getUserDashboardData:",
            normaliseError(error)
          );

          throw error;
        }
      },
      [
        address,
        getReadKtlContract,
        getTokenMetadata,
      ]
    );

  /* =========================================================
     TIER
  ========================================================= */

  const getTier =
    useCallback(
      async (tierId) => {
        try {
          const contract =
            getReadKtlContract();

          return await contract.getTier(
            tierId
          );
        } catch (error) {
          console.error(
            "getTier:",
            normaliseError(error)
          );

          return null;
        }
      },
      [
        getReadKtlContract,
      ]
    );

  const getAllTiers =
    useCallback(async () => {
      try {
        const contract =
          getReadKtlContract();

        const [
          tier1,
          tier2,
          tier3,
        ] = await Promise.all([
          contract.getTier(1),
          contract.getTier(2),
          contract.getTier(3),
        ]);

        return {
          tier1,
          tier2,
          tier3,
        };
      } catch (error) {
        console.error(
          "getAllTiers:",
          normaliseError(error)
        );

        return {
          tier1: null,
          tier2: null,
          tier3: null,
        };
      }
    }, [
      getReadKtlContract,
    ]);

  const getPendingTierData =
    useCallback(async () => {
      const tiers =
        await getAllTiers();

      const values = [
        {
          id: 1,
          data: tiers.tier1,
        },
        {
          id: 2,
          data: tiers.tier2,
        },
        {
          id: 3,
          data: tiers.tier3,
        },
      ];

      return values.map(
        ({ id, data }) => {
          const qualified =
            Number(
              data?.qualifiedWallets ||
              0
            );

          const minimum =
            Number(
              data?.minWallets ||
              0
            );

          return {
            tierId: id,
            tier: data,
            qualifiedWallets:
              qualified,
            minWallets:
              minimum,
            remainingWallets:
              Math.max(
                minimum -
                  qualified,
                0
              ),
            pending:
              Boolean(
                data?.pending
              ),
            claimable:
              Boolean(
                data?.claimable
              ),
          };
        }
      );
    }, [
      getAllTiers,
    ]);

  /* =========================================================
     REWARDS
  ========================================================= */

  const getEstimatedReward =
    useCallback(
      async (
        leagueId,
        walletAddress = address
      ) => {
        if (
          !walletAddress ||
          !leagueId
        ) {
          return null;
        }

        try {
          const contract =
            getReadKtlContract();

          return await contract.getEstimatedReward(
            leagueId,
            walletAddress
          );
        } catch (error) {
          console.error(
            "getEstimatedReward:",
            normaliseError(error)
          );

          return null;
        }
      },
      [
        address,
        getReadKtlContract,
      ]
    );

  const getEstimatedUsdReward =
    useCallback(
      async (
        leagueId,
        walletAddress,
        kindUsdPrice
      ) => {
        try {
          const contract =
            getReadKtlContract();

          const priceRaw =
            parseUnits(
              String(
                kindUsdPrice || 0
              ),
              18
            );

          const raw =
            await contract.getEstimatedUsdReward(
              leagueId,
              walletAddress,
              priceRaw
            );

          return {
            raw,
            formatted:
              formatUnits(
                raw,
                18
              ),
          };
        } catch (error) {
          console.error(
            "getEstimatedUsdReward:",
            normaliseError(error)
          );

          return {
            raw: 0n,
            formatted: "0",
          };
        }
      },
      [
        getReadKtlContract,
      ]
    );

  const getClaimStatus =
    useCallback(
      async (
        leagueId,
        walletAddress = address
      ) => {
        if (
          !leagueId ||
          !walletAddress
        ) {
          return null;
        }

        try {
          const contract =
            getReadKtlContract();

          return await contract.getClaimStatus(
            leagueId,
            walletAddress
          );
        } catch (error) {
          console.error(
            "getClaimStatus:",
            normaliseError(error)
          );

          return null;
        }
      },
      [
        address,
        getReadKtlContract,
      ]
    );

  const getClaimableReward =
    useCallback(
      async (
        leagueId,
        walletAddress = address
      ) => {
        if (
          !leagueId ||
          !walletAddress
        ) {
          return 0n;
        }

        try {
          const contract =
            getReadKtlContract();

          return await contract.getClaimableReward(
            leagueId,
            walletAddress
          );
        } catch (error) {
          console.error(
            "getClaimableReward:",
            normaliseError(error)
          );

          return 0n;
        }
      },
      [
        address,
        getReadKtlContract,
      ]
    );

  const hasClaimed =
    useCallback(
      async (
        leagueId,
        walletAddress = address
      ) => {
        if (
          !leagueId ||
          !walletAddress
        ) {
          return false;
        }

        try {
          const contract =
            getReadKtlContract();

          return await contract.hasClaimed(
            leagueId,
            walletAddress
          );
        } catch (error) {
          console.error(
            "hasClaimed:",
            normaliseError(error)
          );

          return false;
        }
      },
      [
        address,
        getReadKtlContract,
      ]
    );

  const isClaimable =
    useCallback(
      async (
        leagueId,
        walletAddress = address
      ) => {
        if (
          !leagueId ||
          !walletAddress
        ) {
          return false;
        }

        try {
          const contract =
            getReadKtlContract();

          return await contract.isClaimable(
            leagueId,
            walletAddress
          );
        } catch (error) {
          console.error(
            "isClaimable:",
            normaliseError(error)
          );

          return false;
        }
      },
      [
        address,
        getReadKtlContract,
      ]
    );

  const claimReward =
    useCallback(
      async (leagueId) => {
        try {
          const contract =
            await getWriteKtlContract();

          const transaction =
            await contract.claimReward(
              leagueId
            );

          const receipt =
            await transaction.wait();

          return {
            transaction,
            receipt,
            hash:
              transaction.hash,
          };
        } catch (error) {
          console.error(
            "claimReward:",
            normaliseError(error)
          );

          throw new Error(
            normaliseError(error)
          );
        }
      },
      [
        getWriteKtlContract,
      ]
    );

  /* =========================================================
     LEADERBOARD
  ========================================================= */

  const getLeagueParticipants =
    useCallback(
      async (leagueId) => {
        try {
          const contract =
            getReadKtlContract();

          return await contract.getLeagueParticipants(
            leagueId
          );
        } catch (error) {
          console.error(
            "getLeagueParticipants:",
            normaliseError(error)
          );

          return [];
        }
      },
      [
        getReadKtlContract,
      ]
    );

  const getLeaderboard =
    useCallback(
      async (
        leagueId,
        visibleSize = 50
      ) => {
        try {
          const contract =
            getReadKtlContract();

          const participants =
            await contract.getLeagueParticipants(
              leagueId
            );

          const rows =
            await Promise.all(
              participants.map(
                async (
                  walletAddress
                ) => {
                  const [
                    stats,
                    wallet,
                    estimate,
                  ] =
                    await Promise.all([
                      contract.getLeagueUserStats(
                        leagueId,
                        walletAddress
                      ),

                      contract.getWallet(
                        walletAddress
                      ),

                      contract.getEstimatedReward(
                        leagueId,
                        walletAddress
                      ),
                    ]);

                  return {
                    address:
                      walletAddress,

                    username:
                      wallet.username ||
                      "Anon",

                    kp: stats.kp,

                    volumeUsd:
                      stats.volumeUsd,

                    claimableReward:
                      stats.claimableReward,

                    joined:
                      stats.joined,

                    estimatedReward:
                      estimate.estimatedKind,

                    tierId:
                      Number(
                        estimate.tierId ||
                        wallet.tier ||
                        0
                      ),
                  };
                }
              )
            );

          rows.sort((a, b) => {
            if (
              a.kp !== b.kp
            ) {
              return a.kp > b.kp
                ? -1
                : 1;
            }

            if (
              a.volumeUsd !==
              b.volumeUsd
            ) {
              return a.volumeUsd >
                b.volumeUsd
                ? -1
                : 1;
            }

            return a.address
              .toLowerCase()
              .localeCompare(
                b.address.toLowerCase()
              );
          });

          return rows
            .slice(
              0,
              Number(visibleSize)
            )
            .map(
              (
                row,
                index
              ) => ({
                ...row,
                rank:
                  index + 1,
                tier:
                  index < 10
                    ? 1
                    : index < 50
                      ? 2
                      : 3,
              })
            );
        } catch (error) {
          console.error(
            "getLeaderboard:",
            normaliseError(error)
          );

          return [];
        }
      },
      [
        getReadKtlContract,
      ]
    );

  const getLeagueWinners =
    useCallback(
      async (leagueId) => {
        try {
          const contract =
            getReadKtlContract();

          const result =
            await contract.getLeagueWinners(
              leagueId
            );

          const wallets =
            result[0] || [];

          const amounts =
            result[1] || [];

          return wallets.map(
            (
              walletAddress,
              index
            ) => ({
              address:
                walletAddress,
              amount:
                amounts[index] ||
                0n,
            })
          );
        } catch (error) {
          console.error(
            "getLeagueWinners:",
            normaliseError(error)
          );

          return [];
        }
      },
      [
        getReadKtlContract,
      ]
    );

  /* =========================================================
     HISTORY
  ========================================================= */

  const getClaimHistory =
    useCallback(
      async (
        leagueId,
        walletAddress = address
      ) => {
        if (
          !leagueId ||
          !walletAddress
        ) {
          return [];
        }

        try {
          const contract =
            getReadKtlContract();

          return await contract.getClaimHistory(
            leagueId,
            walletAddress
          );
        } catch (error) {
          console.error(
            "getClaimHistory:",
            normaliseError(error)
          );

          return [];
        }
      },
      [
        address,
        getReadKtlContract,
      ]
    );

  const getAllClaimHistory =
    useCallback(
      async (
        walletAddress = address
      ) => {
        if (!walletAddress) {
          return [];
        }

        try {
          const contract =
            getReadKtlContract();

          const currentLeagueId =
            await contract.currentLeagueId();

          const requests = [];

          for (
            let id = 1n;
            id <= currentLeagueId;
            id += 1n
          ) {
            requests.push(
              contract
                .getClaimHistory(
                  id,
                  walletAddress
                )
                .then(
                  (records) => ({
                    leagueId: id,
                    records:
                      Array.from(
                        records
                      ),
                  })
                )
            );
          }

          const results =
            await Promise.all(
              requests
            );

          return results.flatMap(
            ({
              leagueId,
              records,
            }) =>
              records.map(
                (record) => ({
                  ...record,
                  leagueId:
                    record.leagueId ||
                    leagueId,
                })
              )
          );
        } catch (error) {
          console.error(
            "getAllClaimHistory:",
            normaliseError(error)
          );

          return [];
        }
      },
      [
        address,
        getReadKtlContract,
      ]
    );

  /* =========================================================
     PROFILE
  ========================================================= */

  const setUsername =
    useCallback(
      async (username) => {
        const cleanUsername =
          String(
            username || ""
          ).trim();

        if (!cleanUsername) {
          throw new Error(
            "Username is required"
          );
        }

        try {
          const contract =
            await getWriteKtlContract();

          const transaction =
            await contract.setUsername(
              cleanUsername
            );

          const receipt =
            await transaction.wait();

          return {
            transaction,
            receipt,
            hash:
              transaction.hash,
          };
        } catch (error) {
          console.error(
            "setUsername:",
            normaliseError(error)
          );

          throw new Error(
            normaliseError(error)
          );
        }
      },
      [
        getWriteKtlContract,
      ]
    );

  /* =========================================================
     ADMIN
  ========================================================= */

  const startLeague =
    useCallback(
      async (
        startAt,
        endAt,
        visibleLeaderboardSize
      ) => {
        try {
          const contract =
            await getWriteKtlContract();

          const transaction =
            await contract.startLeague(
              startAt.toString(),
              endAt.toString(),
              visibleLeaderboardSize.toString()
            );

          const receipt =
            await transaction.wait();

          return {
            transaction,
            receipt,
          };
        } catch (error) {
          throw new Error(
            normaliseError(error)
          );
        }
      },
      [
        getWriteKtlContract,
      ]
    );

  const autoStartNextLeague =
    useCallback(
      async (
        visibleLeaderboardSize
      ) => {
        try {
          const contract =
            await getWriteKtlContract();

          const transaction =
            await contract.autoStartNextLeague(
              visibleLeaderboardSize.toString()
            );

          const receipt =
            await transaction.wait();

          return {
            transaction,
            receipt,
          };
        } catch (error) {
          throw new Error(
            normaliseError(error)
          );
        }
      },
      [
        getWriteKtlContract,
      ]
    );

  const finalizeLeague =
    useCallback(async () => {
      try {
        const contract =
          await getWriteKtlContract();

        const transaction =
          await contract.finalizeLeague();

        const receipt =
          await transaction.wait();

        return {
          transaction,
          receipt,
        };
      } catch (error) {
        throw new Error(
          normaliseError(error)
        );
      }
    }, [
      getWriteKtlContract,
    ]);

  const emergencyEndLeague =
    useCallback(async () => {
      try {
        const contract =
          await getWriteKtlContract();

        const transaction =
          await contract.emergencyEndLeague();

        const receipt =
          await transaction.wait();

        return {
          transaction,
          receipt,
        };
      } catch (error) {
        throw new Error(
          normaliseError(error)
        );
      }
    }, [
      getWriteKtlContract,
    ]);

  const depositRewardPool =
    useCallback(
      async (amount) => {
        try {
          const contract =
            await getWriteKtlContract();

          const rewardTokenAddress =
            await contract.rewardToken();

          const token =
            await getTokenContract(
              rewardTokenAddress,
              true
            );

          const decimals =
            Number(
              await token.decimals()
            );

          const rawAmount =
            parseUnits(
              String(amount),
              decimals
            );

          const approveTransaction =
            await token.approve(
              KTL_CONTRACT_ADDRESS,
              rawAmount
            );

          await approveTransaction.wait();

          const depositTransaction =
            await contract.depositRewardPool(
              rawAmount
            );

          const receipt =
            await depositTransaction.wait();

          return {
            approveTransaction,
            depositTransaction,
            receipt,
            amountRaw:
              rawAmount,
          };
        } catch (error) {
          console.error(
            "depositRewardPool:",
            normaliseError(error)
          );

          throw new Error(
            normaliseError(error)
          );
        }
      },
      [
        getTokenContract,
        getWriteKtlContract,
      ]
    );

  const setGlobalTradingGoal =
    useCallback(
      async (usdAmount) => {
        try {
          const contract =
            await getWriteKtlContract();

          const rawAmount =
            parseUnits(
              String(usdAmount),
              USD_DECIMALS
            );

          const transaction =
            await contract.setGlobalTradingGoal(
              rawAmount
            );

          const receipt =
            await transaction.wait();

          return {
            transaction,
            receipt,
          };
        } catch (error) {
          throw new Error(
            normaliseError(error)
          );
        }
      },
      [
        getWriteKtlContract,
      ]
    );

  const setKpPerUsd500 =
    useCallback(
      async (value) => {
        try {
          const contract =
            await getWriteKtlContract();

          const transaction =
            await contract.setKpPerUsd500(
              value.toString()
            );

          const receipt =
            await transaction.wait();

          return {
            transaction,
            receipt,
          };
        } catch (error) {
          throw new Error(
            normaliseError(error)
          );
        }
      },
      [
        getWriteKtlContract,
      ]
    );

  const setClaimExpiry =
    useCallback(
      async (seconds) => {
        try {
          const contract =
            await getWriteKtlContract();

          const transaction =
            await contract.setClaimExpiry(
              seconds.toString()
            );

          const receipt =
            await transaction.wait();

          return {
            transaction,
            receipt,
          };
        } catch (error) {
          throw new Error(
            normaliseError(error)
          );
        }
      },
      [
        getWriteKtlContract,
      ]
    );

  const setVisibleLeaderboardSize =
    useCallback(
      async (value) => {
        try {
          const contract =
            await getWriteKtlContract();

          const transaction =
            await contract.setVisibleLeaderboardSize(
              value.toString()
            );

          const receipt =
            await transaction.wait();

          return {
            transaction,
            receipt,
          };
        } catch (error) {
          throw new Error(
            normaliseError(error)
          );
        }
      },
      [
        getWriteKtlContract,
      ]
    );

  const setTierMinimums =
    useCallback(
      async (
        tier1,
        tier2,
        tier3
      ) => {
        try {
          const contract =
            await getWriteKtlContract();

          const transaction =
            await contract.setTierMinimums(
              tier1.toString(),
              tier2.toString(),
              tier3.toString()
            );

          const receipt =
            await transaction.wait();

          return {
            transaction,
            receipt,
          };
        } catch (error) {
          throw new Error(
            normaliseError(error)
          );
        }
      },
      [
        getWriteKtlContract,
      ]
    );

  const setRewardDistribution =
    useCallback(
      async (
        tier1Bps,
        tier2Bps,
        tier3Bps
      ) => {
        try {
          const total =
            Number(tier1Bps) +
            Number(tier2Bps) +
            Number(tier3Bps);

          if (
            total !== 10000
          ) {
            throw new Error(
              "Reward basis points must total 10000"
            );
          }

          const contract =
            await getWriteKtlContract();

          const transaction =
            await contract.setRewardDistribution(
              tier1Bps.toString(),
              tier2Bps.toString(),
              tier3Bps.toString()
            );

          const receipt =
            await transaction.wait();

          return {
            transaction,
            receipt,
          };
        } catch (error) {
          throw new Error(
            normaliseError(error)
          );
        }
      },
      [
        getWriteKtlContract,
      ]
    );

  const pauseContract =
    useCallback(async () => {
      try {
        const contract =
          await getWriteKtlContract();

        const transaction =
          await contract.pause();

        const receipt =
          await transaction.wait();

        return {
          transaction,
          receipt,
        };
      } catch (error) {
        throw new Error(
          normaliseError(error)
        );
      }
    }, [
      getWriteKtlContract,
    ]);

  const unpauseContract =
    useCallback(async () => {
      try {
        const contract =
          await getWriteKtlContract();

        const transaction =
          await contract.unpause();

        const receipt =
          await transaction.wait();

        return {
          transaction,
          receipt,
        };
      } catch (error) {
        throw new Error(
          normaliseError(error)
        );
      }
    }, [
      getWriteKtlContract,
    ]);

  /* =========================================================
     FORMATTING
  ========================================================= */

  const formatUsd =
    useCallback(
      (rawValue) => {
        return toStringSafe(
          rawValue,
          USD_DECIMALS
        );
      },
      []
    );

  const parseUsd =
    useCallback(
      (value) => {
        return parseUnits(
          String(value || 0),
          USD_DECIMALS
        );
      },
      []
    );

  const formatReward =
    useCallback(
      (
        rawValue,
        decimals = 18
      ) => {
        return toStringSafe(
          rawValue,
          decimals
        );
      },
      []
    );

  /* =========================================================
     PENDING KP
  ========================================================= */

  const getUserPendingKp =
    useCallback(
      async (
        walletAddress = address
      ) => {
        if (!walletAddress) {
          return 0n;
        }

        try {
          const contract =
            getReadKtlContract();

          const breakdown =
            await contract.getWalletKpBreakdown(
              walletAddress
            );

          return (
            breakdown.totalPendingKp ||
            0n
          );
        } catch (error) {
          console.error(
            "getUserPendingKp:",
            normaliseError(error)
          );

          return 0n;
        }
      },
      [
        address,
        getReadKtlContract,
      ]
    );

  /* =========================================================
     RETURN
  ========================================================= */

  return {
    /* Wallet */
    isConnected,
    address,
    chainId,
    walletProvider,

    /* Wallet controls */
    connectWallet,
    disconnectWallet,
    checkWallet,

    /* Constants */
    BSC_CHAIN_ID,
    BSC_RPC,
    KTL_CONTRACT_ADDRESS,
    KIND_TOKEN_ADDRESS,
    USD_DECIMALS,

    /* Providers */
    getReadProvider,
    getBrowserProvider,
    getSigner,

    /* Contracts */
    getReadKtlContract,
    getWriteKtlContract,
    getTokenContract,

    /* Wallet data */
    getConnectedAddress,
    getNativeBalance,
    getTokenMetadata,
    getTokenBalance,

    /* League */
    getCurrentLeagueId,
    getLeague,
    getCurrentLeague,
    getGlobalStats,
    getExtendedGlobalStats,
    getGlobalTradingGoal,
    getRewardTokenAddress,
    getRewardTokenData,

    /* User */
    getWalletData,
    getWalletKpBreakdown,
    getLeagueUserStats,
    getUserDashboardData,

    /* Tier */
    getTier,
    getAllTiers,
    getPendingTierData,

    /* Rewards */
    getEstimatedReward,
    getEstimatedUsdReward,
    getClaimStatus,
    getClaimableReward,
    hasClaimed,
    isClaimable,
    claimReward,

    /* Leaderboard */
    getLeagueParticipants,
    getLeaderboard,
    getLeagueWinners,

    /* History */
    getClaimHistory,
    getAllClaimHistory,

    /* Profile */
    setUsername,

    /* Admin */
    startLeague,
    autoStartNextLeague,
    finalizeLeague,
    emergencyEndLeague,
    depositRewardPool,
    setGlobalTradingGoal,
    setKpPerUsd500,
    setClaimExpiry,
    setVisibleLeaderboardSize,
    setTierMinimums,
    setRewardDistribution,
    pauseContract,
    unpauseContract,

    /* Formatting */
    formatUsd,
    parseUsd,
    formatReward,
    toNumberSafe,
    normaliseError,

    /* Pending */
    getUserPendingKp,
  };
}

export default useContract;